"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import Navbar from "@/app/components/Navbar";
import Footer from "@/app/components/Footer";
import { Turnstile } from "@marsidev/react-turnstile";
import type { JobPosting } from "@/lib/jobs";

export default function JobPostingClient({ job }: { job: JobPosting }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", linkedin: "", availability: "full-time", message: "" });
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleResumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
      if (![".pdf", ".doc", ".docx"].includes(ext)) {
        setError("Resume must be a PDF, DOC, or DOCX file.");
        e.target.value = "";
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("Resume must be under 5MB.");
        e.target.value = "";
        return;
      }
      setError(null);
    }
    setResumeFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!turnstileToken) {
      setError("Please complete the security verification.");
      return;
    }
    if (!resumeFile) {
      setError("Please attach your resume (PDF, DOC, or DOCX).");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const body = new FormData();
      body.append("role", `${job.title} — ${job.company}`);
      body.append("company", job.company);
      body.append("jobId", job.id);
      Object.entries(form).forEach(([k, v]) => body.append(k, v));
      body.append("turnstileToken", turnstileToken);
      body.append("resume", resumeFile);

      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/job-applications/submit`, {
        method: "POST",
        body,
      });
      const data = await response.json();
      if (response.ok) {
        setSubmitted(true);
      } else {
        setError(data.error || "Something went wrong. Please try again.");
      }
    } catch {
      setError("Failed to connect to the server. Please check your internet connection.");
    } finally {
      setLoading(false);
    }
  };

  const jobPostingSchema = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job.title,
    description: [
      job.intro,
      job.clientScope,
      "Responsibilities: " + job.responsibilities.join("; ") + ".",
      "Requirements: " + job.requirements.join("; ") + ".",
      job.compensationDetail,
    ].join(" "),
    identifier: {
      "@type": "PropertyValue",
      name: job.company,
      value: job.slug,
    },
    datePosted: job.datePosted,
    validThrough: job.validThrough,
    employmentType: ["FULL_TIME", "PART_TIME"],
    hiringOrganization: {
      "@type": "Organization",
      name: job.company === "AureliusCo" ? "Aurelius & Co" : "Agentxinnovations",
      ...(job.company === "AureliusCo"
        ? { sameAs: "https://www.aureliusco.in", logo: "https://www.aureliusco.in/logo.png" }
        : {}),
    },
    jobLocationType: "TELECOMMUTE",
    applicantLocationRequirements: { "@type": "Country", name: "Anywhere" },
    incentiveCompensation: job.incentiveCompensation,
    directApply: true,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.aureliusco.in" },
      { "@type": "ListItem", position: 2, name: "Careers", item: "https://www.aureliusco.in/careers" },
      { "@type": "ListItem", position: 3, name: job.title, item: `https://www.aureliusco.in/careers/${job.slug}` },
    ],
  };

  return (
    <>
      <Navbar />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jobPostingSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <div style={{ background: "#0A0908", minHeight: "100vh", color: "#F5F0E8" }}>
        <main className="max-w-3xl mx-auto px-4 sm:px-8" aria-label={job.title} style={{ paddingTop: "clamp(5rem, 10vw, 8rem)", paddingBottom: "clamp(4rem, 8vw, 6rem)" }}>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-xs uppercase tracking-[0.25em] mb-4 flex items-center gap-2" style={{ color: "#C9A84C", fontFamily: "'Montserrat', sans-serif" }}>
              <Link href="/careers" style={{ color: "rgba(201,168,76,0.6)" }}>Careers</Link>
              <span>/</span>
              {job.company}
            </p>
            <h1 style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "clamp(2rem, 5vw, 3.2rem)", fontWeight: 400, lineHeight: 1.1, color: "#F5F0E8" }}>
              {job.title}
            </h1>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              {job.tagline}
            </p>

            <div className="flex flex-wrap gap-3 mt-6 mb-4">
              {["Remote · Worldwide", "Full-time or Part-time", job.company].map((tag) => (
                <span key={tag} className="text-xs px-3 py-1.5 tracking-wide" style={{ border: "1px solid rgba(201,168,76,0.25)", color: "rgba(245,240,232,0.6)", fontFamily: "'Montserrat', sans-serif" }}>
                  {tag}
                </span>
              ))}
            </div>

            <p className="text-xs tracking-wide mb-14" style={{ color: "rgba(232,180,90,0.7)", fontFamily: "'Montserrat', sans-serif" }}>
              ● Only a few seats left this hiring cycle
            </p>
          </motion.div>

          <section className="mb-12">
            <p className="text-sm leading-relaxed" style={{ color: "rgba(245,240,232,0.6)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              {job.intro}
            </p>
            <p className="text-sm leading-relaxed mt-4" style={{ color: "rgba(245,240,232,0.6)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              {job.clientScope}
            </p>
          </section>

          <section className="mb-12">
            <h2 className="mb-4" style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "1.6rem", fontWeight: 400 }}>
              What you'll do
            </h2>
            <ul className="space-y-2">
              {job.responsibilities.map((r) => (
                <li key={r} className="flex items-start gap-3 text-sm leading-relaxed" style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                  <span className="mt-0.5" style={{ color: "#C9A84C" }}>✓</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="mb-4" style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "1.6rem", fontWeight: 400 }}>
              What we're looking for
            </h2>
            <ul className="space-y-2">
              {job.requirements.map((r) => (
                <li key={r} className="flex items-start gap-3 text-sm leading-relaxed" style={{ color: "rgba(245,240,232,0.55)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
                  <span className="mt-0.5" style={{ color: "rgba(201,168,76,0.5)" }}>–</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mb-16 p-6" style={{ border: "1px solid rgba(201,168,76,0.15)", background: "rgba(201,168,76,0.03)" }}>
            <h2 className="mb-3" style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "1.6rem", fontWeight: 400, color: "#C9A84C" }}>
              {job.compensationHeadline}
            </h2>
            <p className="text-sm leading-relaxed mb-4" style={{ color: "rgba(245,240,232,0.6)", fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              {job.compensationDetail}
            </p>
            <ul className="space-y-1.5">
              {job.perks.map((p) => (
                <li key={p} className="text-xs leading-relaxed" style={{ color: "rgba(245,240,232,0.5)", fontFamily: "'Montserrat', sans-serif" }}>
                  · {p}
                </li>
              ))}
            </ul>
          </section>

          {/* Apply form */}
          <section id="apply" className="mb-16">
            <h2 className="mb-2" style={{ fontFamily: "'Cormorant', Georgia, serif", fontSize: "1.8rem", fontWeight: 400 }}>
              Apply for this role
            </h2>
            <p className="text-xs leading-relaxed mb-6" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}>
              We reply by email — please check your spam/junk folder in case our confirmation and follow-up land there.
            </p>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="py-16 text-center"
                style={{ border: "1px solid rgba(201,168,76,0.2)", background: "rgba(201,168,76,0.04)" }}
              >
                <div className="text-5xl mb-4" style={{ color: "#C9A84C", fontFamily: "'Cormorant', serif" }}>✦</div>
                <h3 className="text-2xl font-light mb-2" style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}>
                  Application Received
                </h3>
                <p className="text-xs tracking-widest mb-2" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}>
                  We'll review it and reach out if there's a fit.
                </p>
                <p className="text-xs" style={{ color: "rgba(232,180,90,0.7)", fontFamily: "'Montserrat', sans-serif" }}>
                  Please check your spam/junk folder for our confirmation email.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                {error && (
                  <div className="px-4 py-3 text-xs mb-2" style={{ background: "rgba(220, 38, 38, 0.1)", border: "1px solid rgba(220, 38, 38, 0.2)", color: "#ef4444", fontFamily: "'Montserrat', sans-serif" }}>
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-xs tracking-widest uppercase mb-2" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}>Name</label>
                    <input id="name" name="name" type="text" required value={form.name} onChange={handleChange} placeholder="Your name" className="w-full px-4 py-3 text-sm outline-none bg-[#F5F0E8]/5 border border-[#C9A84C]/30 text-[#F5F0E8] placeholder:text-[#F5F0E8]/30" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs tracking-widest uppercase mb-2" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}>Email</label>
                    <input id="email" name="email" type="email" required value={form.email} onChange={handleChange} placeholder="your@email.com" className="w-full px-4 py-3 text-sm outline-none bg-[#F5F0E8]/5 border border-[#C9A84C]/30 text-[#F5F0E8] placeholder:text-[#F5F0E8]/30" />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs tracking-widest uppercase mb-2" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}>Phone (optional)</label>
                    <input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="+91..." className="w-full px-4 py-3 text-sm outline-none bg-[#F5F0E8]/5 border border-[#C9A84C]/30 text-[#F5F0E8] placeholder:text-[#F5F0E8]/30" />
                  </div>
                  <div>
                    <label htmlFor="linkedin" className="block text-xs tracking-widest uppercase mb-2" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}>LinkedIn (optional)</label>
                    <input id="linkedin" name="linkedin" type="url" value={form.linkedin} onChange={handleChange} placeholder="linkedin.com/in/you" className="w-full px-4 py-3 text-sm outline-none bg-[#F5F0E8]/5 border border-[#C9A84C]/30 text-[#F5F0E8] placeholder:text-[#F5F0E8]/30" />
                  </div>
                </div>

                <div>
                  <label htmlFor="availability" className="block text-xs tracking-widest uppercase mb-2" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}>Availability</label>
                  <select id="availability" name="availability" value={form.availability} onChange={handleChange} className="w-full px-4 py-3 text-sm outline-none bg-[#F5F0E8]/5 border border-[#C9A84C]/30 text-[#F5F0E8]">
                    <option value="full-time" style={{ background: "#0A0908" }}>Full-time</option>
                    <option value="part-time" style={{ background: "#0A0908" }}>Part-time</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs tracking-widest uppercase mb-2" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}>Why you're a fit</label>
                  <textarea id="message" name="message" required rows={5} value={form.message} onChange={handleChange} placeholder="Sales experience, target markets, relevant background..." className="w-full px-4 py-3 text-sm outline-none resize-none bg-[#F5F0E8]/5 border border-[#C9A84C]/30 text-[#F5F0E8] placeholder:text-[#F5F0E8]/30" />
                </div>

                <div>
                  <label htmlFor="resume" className="block text-xs tracking-widest uppercase mb-2" style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}>Resume (PDF, DOC, or DOCX)</label>
                  <input
                    id="resume"
                    name="resume"
                    type="file"
                    required
                    accept=".pdf,.doc,.docx"
                    onChange={handleResumeChange}
                    className="w-full text-sm outline-none bg-[#F5F0E8]/5 border border-[#C9A84C]/30 text-[#F5F0E8] file:mr-4 file:py-3 file:px-4 file:border-0 file:text-xs file:tracking-widest file:uppercase file:cursor-pointer file:bg-[#C9A84C]/15 file:text-[#C9A84C] cursor-pointer"
                  />
                  <p className="text-[11px] mt-1.5" style={{ color: "rgba(245,240,232,0.3)", fontFamily: "'Montserrat', sans-serif" }}>Max 5MB.</p>
                </div>

                <div className="mb-2">
                  <Turnstile
                    siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
                    onSuccess={(token) => setTurnstileToken(token)}
                    onExpire={() => setTurnstileToken(null)}
                    options={{ theme: "dark", size: "normal" }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 text-xs font-medium tracking-widest uppercase transition-all duration-300 cursor-pointer disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, #C9A84C, #E8D5A3)", color: "#0A0908", fontFamily: "'Montserrat', sans-serif" }}
                >
                  {loading ? "Submitting..." : "Submit Application"}
                </button>
              </form>
            )}
          </section>

          <div className="text-center text-xs" style={{ color: "rgba(245,240,232,0.3)", fontFamily: "'Montserrat', sans-serif" }}>
            <Link href="/careers" style={{ color: "rgba(201,168,76,0.6)" }}>← All open roles</Link>
          </div>
        </main>
      </div>
      <Footer />
    </>
  );
}
